import { useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { Link } from 'react-router-dom'

import { listerRepas, listerSelles, listerSensations } from '../../db/repo'
import { dayKeyOf, formatHeure } from '../../domain/time'
import { BRISTOL_COURT, MEAL_TYPE_LABELS, SYMPTOM_LABELS } from '../../domain/types'
import { Entete, IconeChevron, IconePlus, Puce, Vide } from '../composants'

type Filtre = 'tout' | 'repas' | 'selles' | 'sensations'

interface Entree {
  sorte: 'repas' | 'selle' | 'sensation'
  id: string
  t: number
  titre: string
  detail: string
  lien: string
}

const FMT_JOUR = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
})

export function PageJournal() {
  const [filtre, setFiltre] = useState<Filtre>('tout')

  const repas = useLiveQuery(() => listerRepas(300), [])
  const selles = useLiveQuery(() => listerSelles(300), [])
  const sensations = useLiveQuery(() => listerSensations(300), [])

  if (repas === undefined || selles === undefined || sensations === undefined) {
    return (
      <>
        <Entete titre="Journal" />
        <main className="contenu">
          <p className="doux">Chargement...</p>
        </main>
      </>
    )
  }

  const entrees: Entree[] = []

  if (filtre === 'tout' || filtre === 'repas') {
    for (const m of repas) {
      entrees.push({
        sorte: 'repas',
        id: m.id,
        t: Date.parse(m.at),
        titre: MEAL_TYPE_LABELS[m.type],
        detail: `${m.ingredientsResolus.length} ingrédient${m.ingredientsResolus.length > 1 ? 's' : ''}`,
        lien: `/repas/${m.id}`,
      })
    }
  }

  if (filtre === 'tout' || filtre === 'selles') {
    for (const s of selles) {
      const marques = [
        s.urgence ? 'urgence' : null,
        s.evacuationIncomplete ? 'évacuation incomplète' : null,
        (s.douleur ?? 0) > 0 ? `douleur ${s.douleur}/10` : null,
      ].filter(Boolean)
      entrees.push({
        sorte: 'selle',
        id: s.id,
        t: Date.parse(s.at),
        titre: `Selle — Bristol ${s.bristol} · ${BRISTOL_COURT[s.bristol]}`,
        detail: marques.length > 0 ? marques.join(' · ') : 'Sans particularité',
        lien: `/selles/${s.id}`,
      })
    }
  }

  if (filtre === 'tout' || filtre === 'sensations') {
    for (const e of sensations) {
      entrees.push({
        sorte: 'sensation',
        id: e.id,
        t: Date.parse(e.at),
        titre: SYMPTOM_LABELS[e.type],
        detail: `Intensité ${e.intensite}/10`,
        lien: `/symptomes/${e.id}`,
      })
    }
  }

  entrees.sort((a, b) => b.t - a.t)

  // Regroupement par jour calendaire
  const parJour = new Map<string, Entree[]>()
  for (const e of entrees) {
    const cle = dayKeyOf(e.t)
    const groupe = parJour.get(cle)
    if (groupe) groupe.push(e)
    else parJour.set(cle, [e])
  }

  const compteSelles = (liste: Entree[]) => liste.filter((e) => e.sorte === 'selle').length

  return (
    <>
      <Entete titre="Journal" sousTitre={`${entrees.length} entrée(s)`} />

      <main className="contenu">
        <div className="segments">
          {(
            [
              ['tout', 'Tout'],
              ['repas', 'Repas'],
              ['selles', 'Selles'],
              ['sensations', 'Sensations'],
            ] as const
          ).map(([cle, libelle]) => (
            <button
              key={cle}
              type="button"
              className="segment"
              aria-pressed={filtre === cle}
              onClick={() => setFiltre(cle)}
            >
              {libelle}
            </button>
          ))}
        </div>

        <div className="boutons">
          <Link to="/repas/nouveau" className="btn grandir">
            <IconePlus /> Repas
          </Link>
          <Link to="/selles/nouvelle" className="btn grandir">
            <IconePlus /> Selle
          </Link>
          <Link to="/symptomes/nouveau" className="btn grandir">
            <IconePlus /> Sensation
          </Link>
        </div>

        {parJour.size === 0 ? (
          <Vide>
            Rien à afficher pour le moment.
            <br />
            Commencez par enregistrer un repas.
          </Vide>
        ) : (
          [...parJour.entries()].map(([jour, liste]) => (
            <section key={jour}>
              <div className="ligne ligne--espacee" style={{ marginBottom: 8 }}>
                <h2 style={{ fontSize: '0.92rem', color: 'var(--texte-doux)' }}>
                  {FMT_JOUR.format(new Date(`${jour}T12:00:00`))}
                </h2>
                <div className="ligne" style={{ gap: 6 }}>
                  {compteSelles(liste) > 0 && (
                    <Puce ton="info">{compteSelles(liste)} passage(s)</Puce>
                  )}
                  <Link to={`/jour/${jour}`} className="btn btn--fantome btn--petit">
                    Journal du jour
                  </Link>
                </div>
              </div>
              <ul className="liste">
                {liste.map((e) => (
                  <li key={`${e.sorte}-${e.id}`}>
                    <Link to={e.lien} className="item">
                      <div className="grandir">
                        <div className="item__principal">
                          {formatHeure(new Date(e.t).toISOString())} — {e.titre}
                        </div>
                        <div className="item__secondaire">{e.detail}</div>
                      </div>
                      {e.sorte === 'repas' && <Puce>Repas</Puce>}
                      {e.sorte === 'selle' && <Puce ton="info">Selle</Puce>}
                      {e.sorte === 'sensation' && <Puce ton="alerte">Sensation</Puce>}
                      <span className="chevron">
                        <IconeChevron />
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </main>
    </>
  )
}
